import { isRepairSession } from 'lib/sessionLifecycle'

export const DEFAULT_REPAIR_CAP = 999
export const DEFAULT_BLIND_NEGATIVE_CAP = 999
export const REPAIR_BLIND_POSITIVE_CAP = 0

export const SESSIONS_STEP_PLANNING = 'planning'
export const SESSIONS_STEP_CARS = 'cars'
export const SESSIONS_STEP_PRICING = 'pricing'
export const SESSIONS_STEP_SOLD = 'sold'
export const SESSIONS_STEP_BLIND_CAPS = 'blindCaps'
export const SESSIONS_STEP_CAPS = 'caps'
export const SESSIONS_STEP_CONFIRMATION = 'confirmation'

export const SESSIONS_STEPS_LIST = [
  SESSIONS_STEP_PLANNING,
  SESSIONS_STEP_CARS,
  SESSIONS_STEP_PRICING,
  SESSIONS_STEP_SOLD,
  SESSIONS_STEP_CAPS,
  SESSIONS_STEP_CONFIRMATION,
]

export const SESSIONS_ACTIVE_DEPENDENCIES = {
  planning: [],
  cars: [SESSIONS_STEP_PLANNING],
  pricing: [SESSIONS_STEP_PLANNING, SESSIONS_STEP_CARS],
  sold: [SESSIONS_STEP_PLANNING, SESSIONS_STEP_CARS],
  blindCaps: [SESSIONS_STEP_PLANNING],
  caps: [SESSIONS_STEP_PLANNING],
  confirmation: [
    SESSIONS_STEP_PLANNING,
    SESSIONS_STEP_CARS,
    SESSIONS_STEP_PRICING,
    SESSIONS_STEP_SOLD,
    SESSIONS_STEP_CAPS,
  ],
}

export const SESSIONS_STEP_ACTIVE = 'active'
export const SESSIONS_STEP_DISABLED = 'disabled'
export const SESSIONS_STEP_OPEN = 'open'
export const SESSIONS_STEP_COMPLETED_WITH_ERRORS = 'errors'
export const SESSIONS_STEP_COMPLETED = 'completed'

export const GROUPS_TOTAL = 10

export const getCurrentStepIndex = (currentState) =>
  SESSIONS_STEPS_LIST.findIndex((state) => state === currentState)

export const getNextStep = (currentState) =>
  SESSIONS_STEPS_LIST[getCurrentStepIndex(currentState) + 1]

export const getLastSessionState = (steps) =>
  steps.find(
    (step) =>
      step.state === SESSIONS_STEP_OPEN ||
      step.state === SESSIONS_STEP_COMPLETED_WITH_ERRORS
  )?.step || SESSIONS_STEP_CONFIRMATION

export const checkAssignment = (session) => !!session?.assignmentStartedAt

export const isStepDisabled = (session, step) =>
  SESSIONS_ACTIVE_DEPENDENCIES[step].length &&
  SESSIONS_ACTIVE_DEPENDENCIES[step].some((el) => !session[`${el}AcceptedAt`])

export const getSessionsStepUrl = (sessionId, step) =>
  `/sessions/${sessionId}/${step}`

export const checkPrices = (car, requiredGroups = []) =>
  !(
    !car.prices?.[0] ||
    requiredGroups
      .filter((group) => group)
      .some((group) => !car.prices?.[group])
  )

export const hasRepairInheritedPricing = (session, car) =>
  isRepairSession(session) && checkPrices(car, session?.requiredGroups)

export const hasPricingData = (session, car) =>
  !!car.existsInPricing || hasRepairInheritedPricing(session, car)

const isSet = (value) => value !== undefined && value !== null && value !== ''

const isPositiveCapValue = (value) => isSet(value) && Number(value) > 0

const shouldUseBlindNegativeDefault = (blindCap) =>
  !isSet(blindCap?.blindNegativeCap) && !isSet(blindCap?.secondChoice)

export const resolveCapFirstChoice = (session, cap) => {
  if (isRepairSession(session) && !isSet(cap?.firstChoice)) {
    return DEFAULT_REPAIR_CAP
  }

  return cap?.firstChoice
}

export const resolveBlindPositiveCap = (session, blindCap) => {
  if (isRepairSession(session)) {
    return REPAIR_BLIND_POSITIVE_CAP
  }

  return blindCap?.firstChoice
}

export const resolveBlindNegativeCap = (blindCap) => {
  if (!blindCap || shouldUseBlindNegativeDefault(blindCap)) {
    return DEFAULT_BLIND_NEGATIVE_CAP
  }

  if (isSet(blindCap.secondChoice)) {
    return blindCap.secondChoice
  }

  return blindCap.blindNegativeCap ?? DEFAULT_BLIND_NEGATIVE_CAP
}

export const hasValidSessionCaps = (session) => {
  const validCaps = (session?.caps || []).every((cap) =>
    isRepairSession(session)
      ? isPositiveCapValue(resolveCapFirstChoice(session, cap))
      : !!cap.firstChoice
  )

  const validBlindCaps = (session?.blindCaps || []).every((cap) =>
    isRepairSession(session)
      ? resolveBlindPositiveCap(session, cap) === REPAIR_BLIND_POSITIVE_CAP
      : !!cap.firstChoice
  )

  return validCaps && validBlindCaps
}

export const getRepairCapDefaultsToPersist = (session) => {
  if (!isRepairSession(session)) {
    return []
  }

  const capDefaults = (session?.caps || [])
    .filter((cap) => !isSet(cap.firstChoice))
    .map((cap) => ({
      scope: 'cap',
      partnerId: cap.partnerId,
      type: 'first',
      value: DEFAULT_REPAIR_CAP,
    }))

  const blindPositiveDefaults = (session?.blindCaps || [])
    .filter((blindCap) => blindCap.firstChoice !== REPAIR_BLIND_POSITIVE_CAP)
    .map((blindCap) => ({
      scope: 'blind',
      partnerId: blindCap.partnerId,
      type: 'first',
      value: REPAIR_BLIND_POSITIVE_CAP,
    }))

  const blindNegativeDefaults = (session?.blindCaps || [])
    .filter(shouldUseBlindNegativeDefault)
    .map((blindCap) => ({
      scope: 'blind',
      partnerId: blindCap.partnerId,
      type: 'second',
      value: DEFAULT_BLIND_NEGATIVE_CAP,
    }))

  return [...capDefaults, ...blindPositiveDefaults, ...blindNegativeDefaults]
}
